/**
 * @fileoverview Service worker update utility — the `onUpdateAppFirst` callback
 *               that the localDatabaseManager calls when the running code is
 *               too old to handle the API's expected schema version.
 * @module local-db/services/service-worker-update
 *
 * Responsibilities:
 * - Unregister all existing service workers (clears any stale SW that might
 *   serve a cached old bundle).
 * - Clear all Cache API entries (removes any cached JS/CSS/HTML from the
 *   previous deployment).
 * - Force a hard, cache-busting page navigation so the browser fetches the
 *   fresh bundle from the server instead of using HTTP cache or bfcache.
 * - Watchdog: if the navigation does not commit within 15s, reject so the
 *   manager can set status='error' instead of hanging on the splash screen.
 *
 * Dependencies:
 * - None (browser APIs only: navigator.serviceWorker, caches, window.location).
 *
 * Architecture:
 * - The FireSquirrel service workers are self-destructing — they exist solely
 *   to satisfy Chrome's `navigator.storage.persist()` auto-grant criteria and
 *   do not cache application assets. However, a stale SW from a previous
 *   VitePWA deployment can survive and serve cached assets. This utility
 *   aggressively clears all SWs and caches before reloading.
 * - This function is designed to be passed as the `onUpdateAppFirst` callback
 *   in `LocalDatabaseManagerConfig`. The manager calls it after setting
 *   status='updating-app'. The function throws if the page doesn't navigate
 *   (e.g. browser blocked the navigation), so the manager can set status='error'.
 *
 * Notes:
 * - This is the defensive check (Phase 3), not the proactive update (Phase 9).
 *   The proactive update uses WebSocket notifications to trigger SW updates
 *   before the user hits the race condition. This defensive check is the
 *   last-resort fallback that fires when the race condition is already
 *   detected (old bundle + new API schema version).
 * - The navigation is unconditional — once the manager decides `update-app-first`,
 *   there is no "cancel" path. The user sees a brief "Updating application…"
 *   status in the sync widget, then the page hard-reloads with the new bundle.
 *
 * @errorHandling
 * Strategy:  If the serviceWorker API or Cache API is unavailable, log a
 *            warning and navigate anyway (the browser will fetch fresh assets
 *            regardless). If the navigation does not commit within 15s
 *            (blocked by the browser, beforeunload handler, etc.), reject so
 *            the manager can set status='error'.
 * User Impact: The page hard-reloads. The user briefly sees "Updating application…"
 *            in the sync widget before the reload.
 * Logging:   Console logs with [sw-update] prefix; no sensitive data.
 */

/**
 * @process triggerServiceWorkerUpdateAndReload
 *
 * Purpose:
 * - The `onUpdateAppFirst` callback for the localDatabaseManager. Called when
 *   the running code's compiled schema version is less than the API's expected
 *   schema version — the code is too old to migrate the local DB.
 *
 * Steps:
 * 1. Unregister all service workers (clears stale SWs that might serve old bundles).
 * 2. Clear all Cache API entries (removes cached JS/CSS/HTML).
 * 3. Hard-navigate to a cache-busted URL (`location.replace` with a unique
 *    query param). This is the only reliable way to bypass HTTP cache, bfcache,
 *    and any still-active service worker — a plain `location.reload()` can
 *    still serve a stale bundle.
 *
 * Inputs:
 * - None (browser APIs only).
 *
 * Outputs:
 * - void (never returns — page navigates away).
 *
 * Failure Handling:
 * - If the serviceWorker API is unavailable, skip SW unregister and proceed
 *   to cache clearing + navigation.
 * - If the Cache API is unavailable, skip cache clearing and proceed to navigation.
 * - The navigation itself is the critical step — if it does not commit within
 *   15s, reject with an error so the manager can set status='error'.
 *
 * Idempotency:
 * - NOT REQUIRED — this function is called at most once per page load (the
 *   page navigates away after it runs). Calling it twice would just navigate twice.
 */
export async function triggerServiceWorkerUpdateAndReload(): Promise<void> {
  // ── Step 1: Unregister all service workers ──────────────────────────────
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        console.log(`[sw-update] Unregistering ${registrations.length} service worker(s)`);
        await Promise.all(registrations.map((reg) => reg.unregister()));
        console.log('[sw-update] All service workers unregistered');
      }
    } catch (err) {
      console.warn('[sw-update] SW unregister failed (non-fatal — will navigate anyway):', err);
    }
  } else {
    console.warn('[sw-update] Service Worker API not available — skipping SW unregister');
  }

  // ── Step 2: Clear all Cache API entries ──────────────────────────────────
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      if (cacheNames.length > 0) {
        console.log(`[sw-update] Clearing ${cacheNames.length} cache(s): ${cacheNames.join(', ')}`);
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
        console.log('[sw-update] All caches cleared');
      }
    } catch (err) {
      console.warn('[sw-update] Cache clearing failed (non-fatal — will navigate anyway):', err);
    }
  } else {
    console.warn('[sw-update] Cache API not available — skipping cache clear');
  }

  // ── Step 3: Hard, cache-busting navigation ───────────────────────────────
  // A plain `location.reload()` is not sufficient: it can be satisfied by the
  // back-forward cache (bfcache), an HTTP cache entry that revalidates to 304,
  // or an active service worker that still serves the old bundle. Changing the
  // document URL with a unique query parameter forces the browser to treat the
  // navigation as a brand-new document load, guaranteeing it fetches fresh
  // index.html and the new hashed bundle from the server.
  const reloadUrl = new URL(window.location.href);
  const previousAttempt = Number(reloadUrl.searchParams.get('__fs_update') ?? 0);
  if (previousAttempt > 0 && Date.now() - previousAttempt < 60_000) {
    throw new Error('The freshly loaded application is still incompatible with the expected database schema. Automatic reload stopped to prevent a loop.');
  }
  reloadUrl.searchParams.set('__fs_update', String(Date.now()));
  console.log('[sw-update] Hard-navigating to load fresh bundle');
  window.location.replace(reloadUrl.toString());

  // Keep the database initialization promise pending while the browser commits
  // the navigation. Throwing immediately after location.replace() races the
  // navigation, renders a false local-database error, and can trigger another
  // initialization/reload cycle before the old document unloads.
  //
  // We listen for `pagehide` (fires when the browser starts unloading the
  // current document for any navigation, including location.replace) to clear
  // the watchdog. This eliminates a race condition where a slow network causes
  // the navigation to take >15s: without the pagehide listener, the watchdog
  // would fire a false-positive error before the page actually unloads.
  // If `pagehide` never fires (navigation blocked by beforeunload, browser
  // policy, or a test environment), the watchdog rejects after 15s so the
  // manager can surface status='error' instead of hanging on the splash.
  await new Promise<void>((resolve, reject) => {
    const watchdogId = setTimeout(
      () => reject(new Error('Application update navigation did not commit within 15s — please manually refresh the page.')),
      15_000,
    );
    window.addEventListener('pagehide', () => {
      clearTimeout(watchdogId);
      resolve();
    }, { once: true });
  });
}
